import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { fetchMessages } from '../../redux/whatsappConversation/actions'
import { useSingleTabModal } from '../../../common/hooks/useSingleTabModal'

import CloseIcon from '@mui/icons-material/Close'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import PersonIcon from '@mui/icons-material/Person'
import DragIndicator from '@mui/icons-material/DragIndicator'
import { useSocket, SOCKET_EVENTS, savePendingWhatsappCall, clearPendingWhatsappCall } from 'cfgCommon/socketContext'
import Draggable from 'react-draggable'
import { useIsMobile } from 'Utilities'
import Mic from 'src/images/mic.svg'
import MicOff from 'src/images/mic_off.svg'
import CallEnd from 'src/images/call_end.svg'
import outgoingRingtone from 'src/sounds/ringtone-llamada-saliente.mp3'
import { hideWhatsappCallingModal } from 'src/features/leads/redux/whatsappConversation/actions';

import './CallModal.less'

const CALLING_STATUSES = {
  calling: 'Llamando…',
  inProgres: 'En llamada',
  ended: 'Llamada finalizada',
  failed: 'No se pudo iniciar la llamada',
}

const CLOSE_MODAL_IN_MS = 3000

const MIC_PERMISSION_STATUS = 'Permití el acceso al micrófono para llamar'

const EXPANDED_SIZE = { width: 474, height: 431 }
const VIEWPORT_MARGIN = 16
const MOBILE_MAX_WIDTH = 639
const MOBILE_HEADER_HEIGHT = 50
const MOBILE_COLLAPSED_HORIZONTAL_MARGIN = 12

const MIC_PERMISSION_ERRORS = ['NotAllowedError', 'PermissionDeniedError', 'SecurityError']

// Formatter to change milliseconds into a human-readable mm:ss format
const formatTime = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
  const seconds = (totalSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
};


const isMobileViewport = () => window.innerWidth <= MOBILE_MAX_WIDTH

const getExpandedPosition = () => {
  if (isMobileViewport()) return { x: 0, y: 0 }

  return {
    x: Math.max(0, Math.round((window.innerWidth - EXPANDED_SIZE.width) / 2)),
    y: Math.max(0, Math.round((window.innerHeight - EXPANDED_SIZE.height) / 2)),
  }
}

const getCollapsedPosition = () => {
  if (!isMobileViewport()) {
    return {
      x: VIEWPORT_MARGIN,
      y: VIEWPORT_MARGIN,
    }
  }

  return {
    x: MOBILE_COLLAPSED_HORIZONTAL_MARGIN,
    y: MOBILE_HEADER_HEIGHT + MOBILE_COLLAPSED_HORIZONTAL_MARGIN,
  }
}

const getLeadDisplayName = lead => {
  if (!lead) return ''
  const firstName = lead.client_firstname || lead.firstname || ''
  const lastName = lead.client_lastname || lead.lastname || ''
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || lead.client_name || lead.fullName || ''
}

const isMicPermissionError = err => MIC_PERMISSION_ERRORS.includes(err?.name)

const getMicrophonePermissionState = async () => {
  try {
    const result = await navigator.permissions.query({ name: 'microphone' })
    return result.state
  } catch {
    return null
  }
}

export function WhatsAppCallModal({ companyId, isOpen, onClose }) {
  const isMobile = useIsMobile()
  const dispatch = useDispatch()

  const leadWhatsapp = useSelector(state => state.leads.whatsapp)
  const leadId = leadWhatsapp.lead_id
  const waPhoneId = leadWhatsapp.wa_phone_id
  const name = getLeadDisplayName(leadWhatsapp.lead)
  const phoneNumber = leadWhatsapp.lead?.phone
  const isCallingModalOpen = useSelector((state) => state.leads?.whatsapp?.show_calling_modal || false); 

  useSingleTabModal(isCallingModalOpen, hideWhatsappCallingModal)

  const { socket } = useSocket()

  const nodeRef = useRef(null)
  const peerConnectionRef = useRef(null)
  const localStreamRef = useRef(null)
  const remoteAudioRef = useRef(null)
  const ringtoneRef = useRef(null)
  const callStartedRef = useRef(false)
  const callIdRef = useRef(null)

  const [status, setStatus] = useState(null)

  const [elapsedTime, setElapsedTime] = useState(0);
  const [isCallActive, setIsCallActive] = useState(false);

  const intervalRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [position, setPosition] = useState(getExpandedPosition)
  const [isDragging, setIsDragging] = useState(false)
  const isCollapsedRef = useRef(isCollapsed)
  isCollapsedRef.current = isCollapsed

  const stopRingtone = () => {
    const ringtone = ringtoneRef.current
    if (!ringtone) return
    ringtone.pause()
    ringtone.currentTime = 0
  }

  const startRingtone = () => {
    let ringtone = ringtoneRef.current
    if (!ringtone) {
      ringtone = new Audio(outgoingRingtone)
      ringtone.loop = true
      ringtoneRef.current = ringtone
    }

    ringtone.currentTime = 0
    ringtone.play().catch(err => {
      console.error('No se pudo reproducir el ringtone de llamada saliente', err)
    })
  }

  const cleanupMedia = () => {
    stopRingtone()

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop())
      localStreamRef.current = null
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close()
      peerConnectionRef.current = null
    }

    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null
    }
  }

  const rememberCallId = callId => {
    if (!callId) return
    callIdRef.current = callId
    savePendingWhatsappCall({
      company_id: companyId,
      wa_phone_id: waPhoneId,
      callId,
    })
  }

  const emitEndCall = ({ clearPending = true } = {}) => {
    const callId = callIdRef.current
    if ((!callStartedRef.current && !callId) || !socket) {
      if (clearPending) clearPendingWhatsappCall()
      return
    }

    if (callId) {
      socket.emit(SOCKET_EVENTS.WHATSAPP_END_CALL, {
        company_id: companyId,
        wa_phone_id: waPhoneId,
        callId,
      })
    }

    callStartedRef.current = false
    callIdRef.current = null
    if (clearPending) clearPendingWhatsappCall()
  }

  const resetUiState = ({ preserveCollapsed = false } = {}) => {
    setStatus(null)
    if (!preserveCollapsed) {
      setIsCollapsed(false)
      setPosition(getExpandedPosition())
    }
    setIsMuted(false)
    setIsDragging(false)
    callStartedRef.current = false
    callIdRef.current = null
    clearPendingWhatsappCall()
  }

  const handleTimerReset = () => {
    setIsCallActive(false);
    setElapsedTime(0);
    startTimeRef.current = Date.now();
  };

  const closeModal = ({ endActiveCall = false } = {}) => {
    if (endActiveCall) emitEndCall()
    cleanupMedia()
    // Si está contraído, conservar tamaño y posición para no expandir al finalizar
    resetUiState({ preserveCollapsed: isCollapsedRef.current })
    setStatus(CALLING_STATUSES.ended)
    handleTimerReset()

    setTimeout(() => { dispatch(fetchMessages(leadId)) }, 2000)

    setTimeout(() => {
      onClose()
    }, CLOSE_MODAL_IN_MS)
  }

  const toggleCollapsed = () => {
    const nextCollapsed = !isCollapsed
    setIsCollapsed(nextCollapsed)
    setPosition(nextCollapsed ? getCollapsedPosition() : getExpandedPosition())
  }

  const handleDragStart = () => {
    setIsDragging(true)
  }

  const handleDrag = (_event, data) => {
    setPosition({ x: data.x, y: data.y })
  }

  const handleDragStop = (_event, data) => {
    setIsDragging(false)
    setPosition({ x: data.x, y: data.y })
  }

  const initWebRTC = async () => {
    const localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
    localStreamRef.current = localStream

    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    })
    peerConnectionRef.current = pc

    localStream.getTracks().forEach(track => pc.addTrack(track, localStream))

    pc.ontrack = event => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = event.streams[0]
      }
    }

    return pc
  }

  const handleStartCall = async () => {
    if (!phoneNumber?.trim()) return

    try {
      const permissionState = await getMicrophonePermissionState()
      if (permissionState === 'denied') {
        setStatus(MIC_PERMISSION_STATUS)
        return
      }

      setStatus(
        permissionState === 'granted'
          ? CALLING_STATUSES.calling
          : MIC_PERMISSION_STATUS
      )

      const pc = await initWebRTC()
      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)

      setStatus(CALLING_STATUSES.calling)
      socket.emit(SOCKET_EVENTS.WHATSAPP_INITIATE_OUTBOUND_CALL, {
        toNumber: phoneNumber,
        sdp: offer.sdp,
        company_id: companyId,
        wa_phone_id: waPhoneId,
      })

      callStartedRef.current = true
    } catch (err) {
      console.error(err)
      cleanupMedia()

      if (isMicPermissionError(err)) {
        setStatus(MIC_PERMISSION_STATUS)
        return
      }

      handleTimerReset()
      setStatus(CALLING_STATUSES.failed)
    }
  }

  useEffect(() => {
    if (!isOpen || !socket) return undefined

    let cancelled = false

    const onFailed = () => {
      setStatus(CALLING_STATUSES.failed)
    }

    const onConnected = async data => {
      if (peerConnectionRef.current) {
        await peerConnectionRef.current.setRemoteDescription(
          new RTCSessionDescription({ type: 'answer', sdp: data.whatsappSdp })
        )
      }

      rememberCallId(data.callId)
    }

    const onRinging = data => {
      rememberCallId(data?.callId)
      startRingtone()
    }

    const onAnswered = data => {
      stopRingtone()
      setStatus(CALLING_STATUSES.inProgres)
      setIsCallActive(true)
      rememberCallId(data.callId)
    }

    const onClientEnded = data => {
      console.log('[debug] onClientEnded data', data)
      closeModal( { endActiveCall: true })
    }

    socket.on(SOCKET_EVENTS.WHATSAPP_INITIATE_OUTBOUND_CALL_FAILED, onFailed)
    socket.on(SOCKET_EVENTS.WHATSAPP_OUTBOUND_CALL_CONNECTED, onConnected)
    socket.on(SOCKET_EVENTS.WHATSAPP_OUTBOUND_CALL_RINGING, onRinging)
    socket.on(SOCKET_EVENTS.WHATSAPP_OUTBOUND_CALL_ANSWERED, onAnswered)
    socket.on(SOCKET_EVENTS.WHATSAPP_CLIENT_ENDED_CALL, onClientEnded)

    handleStartCall().then(() => {
      if (cancelled) cleanupMedia()
    })

    return () => {
      cancelled = true
      socket.off(SOCKET_EVENTS.WHATSAPP_INITIATE_OUTBOUND_CALL_FAILED, onFailed)
      socket.off(SOCKET_EVENTS.WHATSAPP_OUTBOUND_CALL_CONNECTED, onConnected)
      socket.off(SOCKET_EVENTS.WHATSAPP_OUTBOUND_CALL_RINGING, onRinging)
      socket.off(SOCKET_EVENTS.WHATSAPP_OUTBOUND_CALL_ANSWERED, onAnswered)
      socket.off(SOCKET_EVENTS.WHATSAPP_CLIENT_ENDED_CALL, onClientEnded)
      // No borrar sessionStorage acá: socketContext lo necesita después del F5 para cortar la llamada.
      emitEndCall({ clearPending: false })
      cleanupMedia()
    }
  }, [socket, isOpen])

  useEffect(() => {
    if (isCallActive) {
      // Synchronize with actual time to prevent interval drift
      startTimeRef.current = Date.now() - elapsedTime;

      intervalRef.current = setInterval(() => {
        setElapsedTime(Date.now() - startTimeRef.current);
      }, 100); // Updates every 100ms for responsiveness
    }

    return () => clearInterval(intervalRef.current);
  }, [isCallActive]);

  useEffect(() => {
    if (!isOpen || isCollapsed) return undefined

    const syncExpandedPosition = () => {
      setPosition(getExpandedPosition())
    }

    syncExpandedPosition()
    window.addEventListener('resize', syncExpandedPosition)
    return () => window.removeEventListener('resize', syncExpandedPosition)
  }, [isOpen, isCollapsed, isMobile])

  useEffect(() => {
    if (isOpen) return
    resetUiState()
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return undefined

    // Sin end_call: descargar un reporte también ejecuta este handler y cortaba la llamada.
    const handlePageExit = () => {
      const callId = callIdRef.current
      if (!callId) return
      savePendingWhatsappCall({
        company_id: companyId,
        wa_phone_id: waPhoneId,
        callId,
      })
    }

    window.addEventListener('pagehide', handlePageExit)
    window.addEventListener('beforeunload', handlePageExit)
    return () => {
      window.removeEventListener('pagehide', handlePageExit)
      window.removeEventListener('beforeunload', handlePageExit)
    }
  }, [isOpen, companyId, waPhoneId])

  const muteMic = () => {
    const nextMuted = !isMuted
    const localStream = localStreamRef.current
    if (localStream) {
      localStream.getAudioTracks().forEach(track => {
        track.enabled = !nextMuted
      })
    }
    setIsMuted(nextMuted)
  }

  if (!isOpen || status == null) return null

  const isCallEnded = status === CALLING_STATUSES.ended
  const isMicPermissionUi = status === MIC_PERMISSION_STATUS
  const showCallControls = !isMicPermissionUi && !isCallEnded

  const modalClassName = [
    'whatsapp-call-modal',
    isCollapsed && 'whatsapp-call-modal--collapsed',
    isDragging && 'whatsapp-call-modal--dragging',
  ]
    .filter(Boolean)
    .join(' ')

  const disableDrag = isMobile && !isCollapsed

  return (
    <Draggable
      nodeRef={nodeRef}
      bounds="body"
      disabled={disableDrag}
      handle={isMobile ? '.whatsapp-call-modal__drag-handle' : undefined}
      cancel=".no-drag"
      position={position}
      onStart={handleDragStart}
      onDrag={handleDrag}
      onStop={handleDragStop}
    >
      <div ref={nodeRef} className={modalClassName}>
        <div className="whatsapp-call-modal__drag-handle" aria-label="Arrastrar llamada">
          <DragIndicator fontSize="small" />
        </div>
        <div className="whatsapp-call-modal__header">
          <span className="whatsapp-call-modal__brand">WhatsApp</span>
          <div className="whatsapp-call-modal__header-actions">
            {isMicPermissionUi ? (
              <button
                type="button"
                className="whatsapp-call-modal__icon-btn no-drag"
                onClick={onClose}
                aria-label="Cerrar"
              >
                <CloseIcon fontSize="small" />
              </button>
            ) : (
              <button
                type="button"
                className="whatsapp-call-modal__icon-btn no-drag"
                onClick={toggleCollapsed}
                aria-label={isCollapsed ? 'Expandir llamada' : 'Contraer llamada'}
              >
                {isCollapsed ? (
                  <OpenInFullIcon fontSize="small" />
                ) : (
                  <CloseFullscreenIcon fontSize="small" />
                )}
              </button>
            )}
          </div>
        </div>

        <div className="whatsapp-call-modal__body">
          <div className="whatsapp-call-modal__avatar" aria-hidden="true">
            <PersonIcon fontSize="medium" />
          </div>
          <h3 className="whatsapp-call-modal__name">{name}</h3>
          <p className="whatsapp-call-modal__status">{ status === CALLING_STATUSES.inProgres ? formatTime(elapsedTime) : status }</p>
        </div>

        {showCallControls && (
          <div className="whatsapp-call-modal__footer">
            <button
              type="button"
              className={[
                'whatsapp-call-modal__mic-btn',
                'no-drag',
                isMuted && 'whatsapp-call-modal__mic-btn--muted',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={muteMic}
              aria-label={isMuted ? 'Activar micrófono' : 'Mutear micrófono'}
              aria-pressed={isMuted}
            >
              <img src={isMuted ? MicOff : Mic} alt="" />
            </button>
            <button
              type="button"
              className="whatsapp-call-modal__hangup-btn no-drag"
              onClick={() => closeModal({ endActiveCall: true })}
              aria-label="Cortar llamada"
            >
              <img src={CallEnd} alt="" />
            </button>
          </div>
        )}

        <audio ref={remoteAudioRef} autoPlay className="whatsapp-call-modal__remote-audio" />
      </div>
    </Draggable>
  )
}

WhatsAppCallModal.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}
